import { Children, useEffect, useRef, useState } from 'react';
import styles from './SplitSection.module.css';
import LogoLoop from '../LogoLoop/LogoLoop';
import type { LogoItem } from '../LogoLoop/LogoLoop';

interface SplitSectionProps {
  left?: React.ReactNode;
  right?: React.ReactNode;
  className?: string;
  id?: string;
}

type ProjectShowcase = {
  file: string;
  alt: string;
};

const projectImages: ProjectShowcase[] = [
  {
    file: 'projeto-05.png',
    alt: 'Projeto 5',
  },
  {
    file: 'projeto-03.png',
    alt: 'Projeto Pro Pack Uniformes',
  },
  {
    file: 'projeto-01.png',
    alt: 'Projeto Studio Bella',
  },
  {
    file: 'projeto-06.png',
    alt: 'Projeto 6',
  },
  {
    file: 'projeto-02.png',
    alt: 'Projeto Estilo Certo',
  },
  {
    file: 'projeto-04.png',
    alt: 'Projeto Daniela Antunes',
  },
];
const brandLogos = ['em-01.png', 'em-02.png', 'em-03.png', 'em-04.png', 'em-05.png', 'em-06.png'];
const brandLogoItems: LogoItem[] = brandLogos.map((logo, index) => ({
  src: `/${logo}`,
  alt: `Empresa ${index + 1}`,
}));

export default function SplitSection({ left, right, className = '', id }: SplitSectionProps) {
  const galleryRef = useRef<HTMLDivElement | null>(null);
  const metricsRef = useRef<{ step: number }>({ step: 0 });
  const [activeSlide, setActiveSlide] = useState(0);
  const hasRight = Children.count(right) > 0;

  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    let pointerDown = false;
    let startX = 0;
    let scrollStart = 0;

    const computeMetrics = () => {
      const cards = gallery.querySelectorAll<HTMLElement>('[data-project-card]');
      if (cards.length < 2) {
        metricsRef.current.step = cards[0]?.clientWidth ?? gallery.clientWidth;
        return;
      }

      const firstRect = cards[0].getBoundingClientRect();
      const secondRect = cards[1].getBoundingClientRect();
      const step = Math.abs(secondRect.left - firstRect.left);
      metricsRef.current.step = step > 0 ? step : cards[0].clientWidth;
    };

    computeMetrics();

    let scrollFrame: number | null = null;

    const handleScroll = () => {
      const step = metricsRef.current.step;
      if (!step) return;
      const rawIndex = Math.round(gallery.scrollLeft / step);
      const clamped = Math.max(0, Math.min(projectImages.length - 1, rawIndex));
      setActiveSlide(prev => (prev === clamped ? prev : clamped));
    };

    const onScroll = () => {
      if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(handleScroll);
    };

    handleScroll();

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      pointerDown = true;
      startX = event.clientX;
      scrollStart = gallery.scrollLeft;
      gallery.classList.add(styles.isDragging);
      gallery.focus({ preventScroll: true });
      gallery.setPointerCapture?.(event.pointerId);
      event.preventDefault();
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!pointerDown) return;
      const deltaX = event.clientX - startX;
      gallery.scrollLeft = scrollStart - deltaX;
      event.preventDefault();
    };

    const endDrag = (event: PointerEvent) => {
      if (!pointerDown) return;
      pointerDown = false;
      gallery.classList.remove(styles.isDragging);
      gallery.releasePointerCapture?.(event.pointerId);
      event.preventDefault();
    };

    gallery.addEventListener('pointerdown', handlePointerDown);
    gallery.addEventListener('pointermove', handlePointerMove);
    gallery.addEventListener('pointerup', endDrag);
    gallery.addEventListener('pointerleave', endDrag);
    gallery.addEventListener('pointercancel', endDrag);
    gallery.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', computeMetrics);

    return () => {
      gallery.removeEventListener('pointerdown', handlePointerDown);
      gallery.removeEventListener('pointermove', handlePointerMove);
      gallery.removeEventListener('pointerup', endDrag);
      gallery.removeEventListener('pointerleave', endDrag);
      gallery.removeEventListener('pointercancel', endDrag);
      gallery.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', computeMetrics);
      if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
    };
  }, []);

  return (
    <section id={id} className={`${styles.split} ${className}`} aria-label="Seção dividida 50/50">
      <div className={styles.side + ' ' + styles.left}>
        <div className={styles.inner}>
          {left}
          <div className={styles.brandsCard} aria-label="Empresas atendidas">
            <LogoLoop
              logos={brandLogoItems}
              speed={45}
              direction="left"
              logoHeight={34}
              gap={64}
              hoverSpeed={0}
              className="logoloop--force-motion"
              ariaLabel="Empresas atendidas"
            />
          </div>
          <div className={styles.projectsArea}>
            <div
              className={styles.projectsGallery}
              aria-label="Cases em destaque"
              role="list"
              tabIndex={0}
              ref={galleryRef}
            >
              {projectImages.map(({ file, alt }) => (
                <div
                  className={styles.projectCard}
                  role="listitem"
                  key={file}
                  data-project-card
                >
                  <div className={styles.projectFrame}>
                    <img
                      src={`/${file}`}
                      alt={alt}
                      className={styles.projectImage}
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className={styles.projectsDots} role="presentation">
              {projectImages.map(({ file }, index) => (
                <span
                  key={file}
                  className={
                    index === activeSlide
                      ? `${styles.projectDot} ${styles.projectDotActive}`
                      : styles.projectDot
                  }
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      {hasRight ? (
        <div className={styles.side + ' ' + styles.right}>
          <div className={styles.inner}>
            {right}
          </div>
        </div>
      ) : null}
    </section>
  );
}
